/**
 * Coach Learning Paths Dashboard Type Definitions
 *
 * Types for coaches to manage and monitor learning paths assigned to their companies.
 */

// ============================================================================
// Enums and Literal Types
// ============================================================================

/**
 * Status of a learning path
 */
export type PathStatus = "draft" | "published" | "archived";

/**
 * Progress status for a learner's journey through a path
 */
export type ProgressStatus =
  | "not_started"
  | "in_progress"
  | "completed"
  | "overdue";

/**
 * Source of a path assignment
 */
export type AssignmentSource =
  | "coach_assigned"
  | "self_enrolled"
  | "auto_assigned";

/**
 * Trend direction for metrics
 */
export type TrendDirection = "up" | "down" | "neutral";

/**
 * Sort options for the dashboard
 */
export type PathSortField =
  | "name"
  | "enrollmentCount"
  | "completionRate"
  | "lastActivity"
  | "createdAt";

/**
 * Filter options for path status
 */
export type PathFilterStatus =
  | "all"
  | "active"
  | "completed"
  | "needsAttention";

// ============================================================================
// Core Domain Models
// ============================================================================

/**
 * The user who assigned an enrollment or path. Null when the path was only
 * ever self-enrolled (no assigner exists).
 */
export interface AssignedByInfo {
  userId: number;
  firstName: string;
  lastName: string;
}

/**
 * Summary of a learning path for coach dashboard view
 */
export interface CoachLearningPathSummary {
  id: number;
  name: string;
  description: string;
  status: PathStatus;
  createdAt: string;
  updatedAt: string;

  // Enrollment metrics
  totalEnrollments: number;
  activeEnrollments: number;
  completedEnrollments: number;

  // Completion metrics
  averageCompletionRate: number;
  averageTimeToComplete: number | null; // in days

  // Path structure
  totalCourses: number;
  totalMilestones: number;

  // Activity tracking
  lastActivityAt: string | null;
}

/**
 * Detailed learning path information for coach drill-down
 */
export interface CoachLearningPathDetail extends CoachLearningPathSummary {
  // Full path structure
  courses: PathCourseInfo[];
  milestones: PathMilestoneInfo[];

  // All enrollments with progress
  enrollments: LearnerEnrollmentInfo[];

  // Aggregated statistics
  statistics: PathStatistics;

  // Historical trends
  progressTrends: ProgressTrendPoint[];
}

/**
 * Course information within a learning path
 */
export interface PathCourseInfo {
  id: number;
  courseId: number;
  courseTitle: string;
  sequenceOrder: number;
  prerequisiteCourseIds: number[];

  // Aggregate progress
  completionCount: number;
  averageScore: number | null;
}

/**
 * Milestone information within a learning path
 */
export interface PathMilestoneInfo {
  id: number;
  name: string;
  description: string;
  sequenceOrder: number;
  icon?: string;
  /** Always null from the backend (no color column exists yet). */
  color?: string | null;

  // Achievement tracking
  achievedCount: number;
  achievedPercentage: number;
}

/**
 * Individual learner's enrollment and progress information
 */
export interface LearnerEnrollmentInfo {
  enrollmentId: number;
  pathId: number;

  // Learner info
  userId: number;
  firstName: string;
  lastName: string;
  email: string;
  companyId: number;
  companyName: string;
  /** The company's Intacct id, when it has one. */
  companyIntacctId?: string | null;

  // Enrollment details
  enrolledAt: string;
  source: AssignmentSource;
  assignedBy?: AssignedByInfo | null;

  // Progress tracking
  progressStatus: ProgressStatus;
  completionPercentage: number;
  /**
   * Path item ids (same id space as PathCourseInfo.id), NOT LMS course ids —
   * map via PathCourseInfo.courseId when the LMS id is needed.
   */
  completedCourseIds: number[];
  /** Path item id of the enrollment's current item (see completedCourseIds). */
  currentCourseId?: number | null;
  currentCourseTitle?: string | null;
  achievedMilestoneIds: number[];

  // Timeline
  /** First real activity (path opened or first completion); null = never opened. */
  startedAt: string | null;
  lastActivityAt: string | null;
  completedAt: string | null;
  /** The enrollment due date (the backend has no real completion estimate). */
  estimatedCompletionDate: string | null;

  // Performance
  /** Always null today (no scoring rollup). */
  averageScore: number | null;
  /** Active time (tab-visible + interacting), from the activity stream. */
  timeSpentMinutes: number;
  /** Runs of activity with no gap over 30 min. */
  sessionCount: number;
  /** Last on-demand reminder a coach sent from the board; null = never. */
  lastNudgedAt?: string | null;
}

/**
 * Per-item funnel / dwell / drop-off for one path
 * (GET /api/private/coaching/learning-paths/{pathId}/item-analytics).
 */
export interface PathItemAnalyticsRow {
  itemId: number;
  stepId: number | null;
  type: string;
  title: string;
  position: number;
  optional: boolean;
  estimatedMinutes: number | null;
  /** Distinct enrollments that opened the item. */
  viewedCount: number;
  completedCount: number;
  /** viewed - completed, floored at 0. */
  dropOffCount: number;
  medianActiveSeconds: number | null;
  averageActiveSeconds: number | null;
  timedLearnerCount: number;
  /** Median dwell is more than twice the author's estimate. */
  overEstimate: boolean;
  /** Incomplete enrollments whose last activity was on this item. */
  stalledCount: number;
  stalled: Array<{
    enrollmentId: number;
    userId: number;
    lastActivityAt: string | null;
  }>;
}

/**
 * One Sankey edge. Node keys are "enrolled", "step:<id>", "finished",
 * "paused" or "never_opened".
 */
export interface PathFlowLink {
  from: string;
  to: string;
  learners: number;
}

export interface PathItemAnalytics {
  pathId: number;
  enrollmentCount: number;
  startedCount: number;
  completedCount: number;
  items: PathItemAnalyticsRow[];
  flow: {
    /** path_steps in sort order, the Sankey's columns. */
    steps: Array<{ id: number; label: string }>;
    links: PathFlowLink[];
  };
}

/**
 * Aggregated statistics for a learning path
 */
export interface PathStatistics {
  // Enrollment metrics
  totalEnrollments: number;
  activeEnrollments: number;
  completedEnrollments: number;
  overdueEnrollments: number;

  // Completion metrics
  averageCompletionRate: number;
  medianCompletionRate: number;
  averageTimeToCompleteDays: number | null;

  // Engagement metrics
  averageSessionsPerLearner: number;
  averageTimeSpentMinutes: number;
  lastSevenDaysActiveUsers: number;

  // Performance metrics
  averageScore: number | null;
  topPerformersCount: number; // Learners in top 10%
  atRiskCount: number; // Learners with no activity in 14+ days
}

/**
 * Progress trend data point for charts
 */
export interface ProgressTrendPoint {
  date: string;
  enrollments: number;
  completions: number;
  averageProgress: number;
  activeUsers: number;
}

// ============================================================================
// Company-Level Aggregation
// ============================================================================

/**
 * Learning path progress aggregated by company
 */
export interface CompanyPathProgress {
  companyId: number;
  companyName: string;

  // Assigned paths
  assignedPaths: CompanyAssignedPath[];

  // Aggregated metrics
  totalEnrollments: number;
  completedEnrollments: number;
  overallCompletionRate: number;
  averageProgress: number;

  // Activity
  activeLearnersLast7Days: number;
  lastActivityAt: string | null;
}

/**
 * Path assignment at company level
 */
export interface CompanyAssignedPath {
  pathId: number;
  pathName: string;
  assignedAt: string;
  /** Null for self-enrollment-only paths (no assigner exists). */
  assignedBy: AssignedByInfo | null;

  // Company-specific metrics
  enrolledCount: number;
  completedCount: number;
  inProgressCount: number;
  notStartedCount: number;
  averageCompletionRate: number;
}

// ============================================================================
// Dashboard State Models
// ============================================================================

/**
 * Redux state shape for coach learning paths dashboard
 */
export interface CoachLearningPathsDashboardState {
  // Path listings
  paths: CoachLearningPathSummary[];
  pathsLoading: boolean;
  pathsError: string | null;

  // Selected path detail
  selectedPathId: number | null;
  selectedPathDetail: CoachLearningPathDetail | null;
  selectedPathLoading: boolean;
  selectedPathError: string | null;

  // Company view
  companyProgress: CompanyPathProgress[];
  companyProgressLoading: boolean;
  companyProgressError: string | null;

  // Learners view
  learners: LearnerEnrollmentInfo[];
  learnersLoading: boolean;
  learnersError: string | null;
  learnersPagination: PaginationInfo;

  // UI state
  filters: DashboardFilters;
  viewMode: "paths" | "companies" | "learners" | "engagement";

  // Pagination
  pagination: PaginationInfo;
}

/**
 * Dashboard filter state
 */
export interface DashboardFilters {
  searchQuery: string;
  statusFilter: PathFilterStatus;
  sortBy: PathSortField;
  sortDirection: "asc" | "desc";
  companyIds: number[];
  /** Server-side learners filter (GET /coaching/learners pathIds[]). */
  pathIds: number[];
}

/**
 * Pagination information
 */
export interface PaginationInfo {
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
}

// ============================================================================
// API Request/Response Types
// ============================================================================

/**
 * Request to fetch coach learning paths.
 * Scope is always derived from the token server-side; companyIds is honored
 * for super admins only (coaches are pinned to their active company).
 */
export interface GetCoachPathsRequest {
  companyIds?: number[];
  status?: PathStatus[];
  search?: string;
  sortBy?: PathSortField;
  sortDirection?: "asc" | "desc";
  page?: number;
  pageSize?: number;
}

/**
 * Response for coach learning paths list
 */
export interface GetCoachPathsResponse {
  paths: CoachLearningPathSummary[];
  pagination: PaginationInfo;
}

/**
 * Request to fetch path detail
 */
export interface GetPathDetailRequest {
  pathId: number;
  includeEnrollments?: boolean;
  includeTrends?: boolean;
  enrollmentFilters?: {
    status?: ProgressStatus[];
    companyIds?: number[];
    search?: string;
  };
}

/**
 * Request to fetch company path progress.
 * companyIds is honored for super admins only (coaches are pinned to their
 * active company server-side).
 */
export interface GetCompanyPathProgressRequest {
  companyIds?: number[];
}

/**
 * Response for company path progress
 */
export interface GetCompanyPathProgressResponse {
  companies: CompanyPathProgress[];
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Type guard for valid path status
 */
export function isPathStatus(value: string): value is PathStatus {
  return ["draft", "published", "archived"].includes(value);
}

/**
 * Type guard for valid progress status
 */
export function isProgressStatus(value: string): value is ProgressStatus {
  return ["not_started", "in_progress", "completed", "overdue"].includes(value);
}

/**
 * Calculate if a learner is at risk based on activity
 */
export function isLearnerAtRisk(
  enrollment: LearnerEnrollmentInfo,
  inactiveDaysThreshold: number = 14,
): boolean {
  if (enrollment.progressStatus === "completed") {
    return false;
  }

  if (!enrollment.lastActivityAt) {
    return true;
  }

  const lastActivity = new Date(enrollment.lastActivityAt);
  const now = new Date();
  const daysSinceActivity = Math.floor(
    (now.getTime() - lastActivity.getTime()) / (1000 * 60 * 60 * 24),
  );

  return daysSinceActivity >= inactiveDaysThreshold;
}

/**
 * Get display label for progress status
 */
export function getProgressStatusLabel(status: ProgressStatus): string {
  const labels: Record<ProgressStatus, string> = {
    not_started: "Not Started",
    in_progress: "In Progress",
    completed: "Completed",
    overdue: "Overdue",
  };
  return labels[status];
}

/**
 * Get display color for progress status
 */
export function getProgressStatusColor(
  status: ProgressStatus,
): "default" | "primary" | "success" | "warning" | "danger" {
  const colors: Record<
    ProgressStatus,
    "default" | "primary" | "success" | "warning" | "danger"
  > = {
    not_started: "default",
    in_progress: "primary",
    completed: "success",
    overdue: "danger",
  };
  return colors[status];
}

/**
 * Calculate days until estimated completion
 */
export function getDaysUntilCompletion(
  enrollment: LearnerEnrollmentInfo,
): number | null {
  if (!enrollment.estimatedCompletionDate) {
    return null;
  }

  const estimated = new Date(enrollment.estimatedCompletionDate);
  const now = new Date();
  return Math.ceil(
    (estimated.getTime() - now.getTime()) / (1000 * 60 * 60 * 24),
  );
}

// ============================================================================
// Status Thresholds (shared constants to avoid magic numbers)
// ============================================================================

export const COMPLETION_THRESHOLDS = {
  COMPLETED: 90,
  AT_RISK: 50,
} as const;

/**
 * Determine action plan status from a completion rate and active count.
 * Used by PathSummaryRow, CompanyProgressRow, and other status displays.
 */
export function getActionPlanStatusFromRate(
  completionRate: number,
  activeOrTotalCount: number,
): "completed" | "at_risk" | "on_track" {
  if (completionRate >= COMPLETION_THRESHOLDS.COMPLETED) return "completed";
  if (completionRate < COMPLETION_THRESHOLDS.AT_RISK && activeOrTotalCount > 0)
    return "at_risk";
  return "on_track";
}

/**
 * Format a date string to a human-readable relative time (e.g. "2 days ago").
 * Returns "Never" for null/undefined input.
 */
export function formatRelativeDate(
  dateString: string | null | undefined,
): string {
  if (!dateString) return "Never";

  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
  return date.toLocaleDateString();
}

/**
 * Create empty dashboard filters
 */
export function createEmptyDashboardFilters(): DashboardFilters {
  return {
    searchQuery: "",
    statusFilter: "all",
    sortBy: "lastActivity",
    sortDirection: "desc",
    companyIds: [],
    pathIds: [],
  };
}

/**
 * Create initial dashboard state
 */
export function createInitialDashboardState(): CoachLearningPathsDashboardState {
  return {
    paths: [],
    pathsLoading: false,
    pathsError: null,

    selectedPathId: null,
    selectedPathDetail: null,
    selectedPathLoading: false,
    selectedPathError: null,

    companyProgress: [],
    companyProgressLoading: false,
    companyProgressError: null,

    learners: [],
    learnersLoading: false,
    learnersError: null,
    learnersPagination: {
      page: 1,
      pageSize: 20,
      totalItems: 0,
      totalPages: 0,
    },

    filters: createEmptyDashboardFilters(),
    viewMode: "paths",

    pagination: {
      page: 1,
      pageSize: 20,
      totalItems: 0,
      totalPages: 0,
    },
  };
}
